import type { RecommendedItem } from 'src/components/Shared/RecommendedToolsMaterials/types';
import type { ApplianceType, MediaType, MediaUploadResType } from 'src/helper/shared-types';
import type { StatusType } from '../service-request/types';
import type { MaterialsAPIResponse } from '../work-order/types';

export type CategoryItem = {
  category_id: string;
  label: string;
};

export type SubCategoryItem = {
  sub_category_id: string;
  label: string;
};

export type DiagnosisItem = {
  diagnosis_id: string;
  label: string;
};

export type IssueCategory = {
  category: CategoryItem;
  sub_category: SubCategoryItem;
  diagnosis: DiagnosisItem;
};

export type CategoryFieldType = {
  title: string;
  description: string;
  category?: IssueCategory;
  categoryValue: string;
};

export type IssueTrip = {
  work_order_id: string;
  work_order_number: string;
  work_order_issue_id?: string;
  trip_id: string;
  trip_number: number;
  status: StatusType;
  service_notes?: string;
  media?: MediaType[];
  additional_info?: string;
  additional_notes?: string;
  note_to_provider?: string;
  materials?: MaterialsAPIResponse[];
  schedule_start_time?: string;
  schedule_end_time?: string;
  decline_reason?: string;
  quality_checked_at?: string | null;
  // A display name, or null when nobody reviewed it: an automatic pass stamps
  // the timestamp and leaves the reviewer empty.
  quality_checked_by?: string | null;
  modified_user?: {
    name?: string;
    modified_at?: string;
  };
  appointment?: {
    name?: string;
  };
};

export type ApiIssueResponse = {
  issue_id: string;
  work_order_issue_id?: string;
  service_request_issue_id: string;
  title: string;
  description: string;
  work_orders: Array<{
    work_order_id: string;
    work_order_number: string;
    issues_count?: number;
  }>;
  abilities: Array<string>;
  status: {
    value: string;
    label: string;
  };
  problem: {
    category: {
      category_id: string;
      label: string;
    };
    sub_category: {
      sub_category_id: string;
      label: string;
    };
    diagnosis: {
      diagnosis_id: string;
      label: string;
    };
  };
  trips?: Array<IssueTrip>;
  other_work_order_trips?: Array<IssueTrip>;
  // ORI-680: cancellation photo(s) the backend emits on the web block; carried through the
  // issues reducer's omit-spread onto the coordinator Issue (same shape as `media`).
  cancellation_media?: MediaUploadResType[];
  media?: MediaType[];
  issue_recommended_items?: RecommendedItem[];
  created_at?: string;
};

export type ApiIssueResponseSR = {
  issue_id: string;
  title: string;
  description: string;
  problem: {
    category: {
      category_id: string;
      label: string;
    };
    sub_category: {
      sub_category_id: string;
      label: string;
    };
    diagnosis: {
      diagnosis_id: string;
      label: string;
    };
  };
  status: {
    value: string;
    label: string;
  };
  abilities: string[];
  created_at: string;
  insights: IssueInsight[];
  work_orders: {
    work_order_id: string;
    work_order_number: string;
  }[];
  work_order_issues: ApiIssueResponse[];
  media?: MediaType[];
  // ORI-680: typed for symmetry with ApiIssueResponse. The coordinator gallery only renders on
  // the work-order page, so the SR reducer path never surfaces it — but keep the type honest.
  cancellation_media?: MediaUploadResType[];
  appliances?: ApplianceType[];
  issue_recommended_items?: RecommendedItem[];
};

export type IssueInsight = {
  slug: string;
  title: string;
  description: string;
  issue_id?: number;
  is_property_level?: boolean;
  reference_type?: string;
  reference_id?: string[];
};

export type IssueQuoteTask = {
  quote_task_id: string;
  task_type: 'standard' | 'optional';
  selected_option: {
    option_id: string;
    description: string;
    labor_cost_in_cents: number;
    markup_fee_type: 'percentage' | 'fixed';
    markup_fee_type_value?: string;
    markup_fee_in_cents: number;
    total_cost_in_cents: number;
    media: MediaType[];
    materials: {
      material_id: string;
      label: string;
      markup_fee_type: 'percentage' | 'fixed';
      markup_fee_type_value?: string;
      markup_fee_in_cents: number;
      cost_in_cents?: number;
      total_cost_in_cents: number;
      quantity: number;
      quantity_type: string;
      cost_type: 'per-unit' | 'total';
    }[];
  };
};

export type Issue = {
  issue_id: string;
  service_request_issue_id: string;
  title: string;
  description: string;
  quote_task: IssueQuoteTask | null;
  issue_type: string;
  work_orders: Array<{ work_order_id: string; work_order_number: string; issues_count?: number }>;
  abilities: Array<string>;
  status: {
    value: string;
    label: string;
  };
  problem: {
    category: {
      category_id: string;
      label: string;
    };
    sub_category: {
      sub_category_id: string;
      label: string;
    };
    diagnosis: {
      diagnosis_id: string;
      label: string;
    };
  };
  tripSource: {
    [key: string]: IssueTrip;
  };
  currentWorkOrderTripIds: string[];
  relatedWorkOrderTripIds: string[];
  // 'cancel_trip' is a genuine current cancellation (NOT a decline) — the backend emits it, so
  // model it here and keep it OUT of DECLINE_UNRESOLVE_ORIGINS (see redux/issues/constants.ts).
  unresolved_from?: 'close_workorder' | 'technician_decline' | 'quality_check' | 'cancel_trip';
  // ORI-593: cancellation reason surfaced read-only on cancelled work orders (coordinator
  // work-order view). `cancel_reason` is the free-text detail (nullable);
  // `cancel_reason_label` is the picker label — resolved for every cancel origin (the backend
  // maps the cancel-work-order slugs to their label); `cancelled_by` is "Vendor"/"Coordinator"/etc.
  cancel_reason?: string | null;
  cancel_reason_label?: string | null;
  cancelled_by?: string | null;
  // ORI-680: cancellation photo(s) surfaced read-only alongside the reason on the coordinator
  // work-order view — parity with the Vendor Portal (ORI-639). Same MediaResource shape as
  // `media`; the backend already emits it to coordinator-web.
  cancellation_media?: MediaUploadResType[];
  media?: MediaType[];
  insights?: IssueInsight[];
  appliances?: ApplianceType[];
  recommended_tools?: RecommendedItem[];
  recommended_materials?: RecommendedItem[];
  created_at?: string;
};
export type InitialIssueStateType = {
  issuesSource: {
    [key: string]: Issue;
  };
  isAddingOrEditingIssue: boolean;
  isIssueBeingUnassigned: boolean;
};

export const issueSliceInitialState: InitialIssueStateType = {
  issuesSource: {},
  isAddingOrEditingIssue: false,
  isIssueBeingUnassigned: false,
};
